import { Flex, Box } from '@chakra-ui/react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { forwardRef, ForwardRefExoticComponent, RefAttributes } from 'react';
import ChakraPagination from '@choc-ui/paginator';

import {
  useJobFilters,
  JobFiltersType,
} from 'lib/repositories/jobsShowcaseFilters';
import Button from 'components/atoms/button';

const PreviousPage = forwardRef<HTMLButtonElement>((props, ref) => (
  <Button ref={ref} {...props}>
    <Flex
      backgroundColor='primary.500'
      h='full'
      w='full'
      borderRadius='full'
      justifyContent='center'
      alignItems='center'
    >
      <Box
        as={IoIosArrowBack}
        color='white.100'
        width='20px'
        height='20px'
        ml='-2px'
      />
    </Flex>
  </Button>
));

PreviousPage.displayName = 'PreviousPage';

const PreviousPageDisabled = forwardRef<HTMLButtonElement>((props, ref) => (
  <Button ref={ref} {...props}>
    <Flex
      backgroundColor='grey.600'
      h='full'
      w='full'
      borderRadius='full'
      justifyContent='center'
      alignItems='center'
    >
      <Box
        as={IoIosArrowBack}
        color='white.100'
        width='20px'
        height='20px'
        ml='-2px'
      />
    </Flex>
  </Button>
));

PreviousPageDisabled.displayName = 'PreviousPageDisabled';

const NextPage = forwardRef<HTMLButtonElement>((props, ref) => (
  <Button data-testid='next' ref={ref} {...props}>
    <Flex
      backgroundColor='primary.500'
      h='full'
      w='full'
      borderRadius='full'
      justifyContent='center'
      alignItems='center'
    >
      <Box
        as={IoIosArrowForward}
        color='white.100'
        width='20px'
        height='20px'
        mr='-2px'
      />
    </Flex>
  </Button>
));

NextPage.displayName = 'NextPage';

const NextPageDisabled = forwardRef<HTMLButtonElement>((props, ref) => (
  <Button ref={ref} {...props}>
    <Flex
      backgroundColor='grey.600'
      h='full'
      w='full'
      borderRadius='full'
      justifyContent='center'
      alignItems='center'
    >
      <Box
        as={IoIosArrowForward}
        color='white.100'
        width='20px'
        height='20px'
        mr='-2px'
      />
    </Flex>
  </Button>
));

NextPageDisabled.displayName = 'NextPageDisabled';

const EmptyButton = forwardRef<HTMLButtonElement>((props, ref) => (
  <Button ref={ref} {...props}>
    ...
  </Button>
));

EmptyButton.displayName = 'EmptyButton';

export interface PaginationProps {
  total: number;
  handleSearchJobs: (updatedFilters?: JobFiltersType) => void;
}

export const Pagination = ({ total, handleSearchJobs }: PaginationProps) => {
  const { filters, setPage } = useJobFilters();

  const itemRender = (
    _currentPage?: number,
    type?: string
  ): ForwardRefExoticComponent<RefAttributes<HTMLButtonElement>> | null => {
    if (_currentPage === 1 && type === 'prev') return PreviousPageDisabled;
    if (_currentPage === Math.ceil(total / 10) && type === 'next')
      return NextPageDisabled;
    if (type === 'prev') return PreviousPage;
    if (type === 'next') return NextPage;
    if (type === 'backward' || type === 'forward') return EmptyButton;

    return null;
  };

  const handleOnChange = (page: number | undefined) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setPage(page);
    handleSearchJobs({ ...filters, page });
  };

  return (
    <Flex justify='center' py={8}>
      <ChakraPagination
        current={filters.page}
        responsive={{
          activePage: true,
        }}
        itemRender={itemRender}
        pageNeighbours={2}
        pageSize={10}
        paginationProps={{ display: 'flex' }}
        total={total}
        onChange={handleOnChange}
        baseStyles={{
          bg: 'transparent',
          borderColor: 'transparent',
          borderRadius: '50%',
          borderWidth: 1,
          color: 'text.900',
          lineHeight: 8,
          p: 0,
          w: 8,
        }}
        activeStyles={{
          bg: 'primary.150',
          borderColor: 'primary.500',
        }}
      />
    </Flex>
  );
};
